import { debounce } from '@mui/material/utils'
import { UnknownUserID } from 'components/pages/user/user-display-name'
import {
    AccessReviewSearchServiceEntitlementRollupRef,
    AccessReviewSearchServiceEntitlementRollupRequest,
    AccessReviewAppUserDetailRowSearchRef,
    SearchAppUserRollupRequest,
    AccessReviewSearchServiceUserRollupRef,
    AccessReviewSearchServiceUserRollupRequest,
    AccessReviewAppNavRollupRef,
    SearchAppRollupRequest,
    AccountsWithNoIdentityTaskStatusRequest,
} from 'pbts/c1/api/accessreview/v1/accessreview_search'
import { AccessReviewSearchServiceClient } from 'pbts/c1/api/accessreview/v1/accessreview_search.client'
import { useDispatch } from 'react-redux'
import { appEntKey, aekDecoded } from 'redux_toolkit/lib/TicketActionTracker'
import {
    setRollupCounts,
    incrementCompletedCount,
    setBulkActionInProgress,
    RollupCount,
    updateCountsByAmount,
} from 'redux_toolkit/slices/optimisticRollupCounts'

export const getAppUserCountKey = (appId: string, appUserId: string): string => `${appId}:${appUserId}`

const computeOpenCount = (openTaskCount: number, pendingSubmissionCount: number | undefined): number => {
    return Math.max(0, openTaskCount - (pendingSubmissionCount ?? 0))
}

type EntityType = 'user' | 'app' | 'appUser' | 'appEntitlement'

type EntityIds = {
    userId?: string
    appId?: string
    appUserId?: string
    appEntitlementId?: string
}

export class RollupCountManager {
    private dispatch: ReturnType<typeof useDispatch>
    private accessReviewSearchServiceClient: AccessReviewSearchServiceClient
    private reviewerUserId: string
    private sessionId: string

    // Track dirty entities that need refresh
    private dirtyEntities: Set<string> = new Set()

    // Track in-flight actions per entity
    private ticketActionIdsPerEntity: Record<string, Set<string>> = {}
    private bulkActionIdsPerEntity: Record<string, Set<string>> = {}

    // Entities pending refresh after debounce
    private entitiesToRefresh: Record<string, Set<string>> = {}

    // Track optimistic update timestamps to prevent stale responses
    private optimisticUpdateTimestamps: Record<string, number> = {}

    // Clock for version control (version + timestamp)
    private clock: Record<string, { version: number; timestamp: number }> = {}
    private reqNumber = 0

    // In-flight request deduplication
    private inFlightRequests: Map<string, Promise<void>> = new Map()

    // Debounced refresh function
    private debouncedRefreshFn: ReturnType<typeof debounce>

    constructor(
        dispatch: ReturnType<typeof useDispatch>,
        accessReviewSearchServiceClient: AccessReviewSearchServiceClient,
        reviewerUserId: string,
    ) {
        this.dispatch = dispatch
        this.accessReviewSearchServiceClient = accessReviewSearchServiceClient
        this.reviewerUserId = reviewerUserId
        this.sessionId = Date.now().toString()

        // Create debounced refresh with 2s delay
        this.debouncedRefreshFn = debounce(
            () => {
                this.executeRefresh()
            },
            2000,
        )
    }

    private static getEntityKey(type: EntityType, ids: EntityIds): string {
        switch (type) {
            case 'user':
                return `user:${ids.userId}`
            case 'app':
                return `app:${ids.appId}`
            case 'appUser':
                return `appUser:${ids.appId}:${ids.appUserId}`
            case 'appEntitlement':
                return `appEntitlement:${ids.appId}:${ids.appEntitlementId}`
            default:
                throw new Error(`Unknown entity type: ${type}`)
        }
    }

    public trackAction = (
        accessReviewId: string,
        type: EntityType,
        ids: EntityIds,
        ticketActionId: string,
    ): void => {
        const entityKey = RollupCountManager.getEntityKey(type, ids)
        this.ticketActionIdsPerEntity[entityKey] = this.ticketActionIdsPerEntity[entityKey] || new Set()
        this.ticketActionIdsPerEntity[entityKey].add(ticketActionId)
        this.dirtyEntities.add(`${accessReviewId}:${entityKey}`)
    }

    public untrackAction = (
        accessReviewId: string,
        type: EntityType,
        ids: EntityIds,
        ticketActionId: string,
    ): void => {
        const entityKey = RollupCountManager.getEntityKey(type, ids)
        this.ticketActionIdsPerEntity[entityKey]?.delete(ticketActionId)

        // Schedule refresh
        this.entitiesToRefresh[accessReviewId] = this.entitiesToRefresh[accessReviewId] || new Set()
        this.entitiesToRefresh[accessReviewId].add(entityKey)

        if (this.ticketActionIdsPerEntity[entityKey]?.size === 0) {
            delete this.ticketActionIdsPerEntity[entityKey]
        }

        // Trigger debounced refresh
        this.debouncedRefreshFn()
    }

    public trackBulkAction = (bulkActionId: string, type: EntityType, ids: EntityIds): void => {
        const entityKey = RollupCountManager.getEntityKey(type, ids)
        this.bulkActionIdsPerEntity[entityKey] = this.bulkActionIdsPerEntity[entityKey] || new Set()
        this.bulkActionIdsPerEntity[entityKey].add(bulkActionId)

        // Set bulk action in progress
        const stateId = RollupCountManager.getStateId(type, ids)
        this.dispatch(setBulkActionInProgress({ type, id: stateId, inProgress: true }))
    }

    public untrackBulkAction = (
        accessReviewId: string,
        type: EntityType,
        ids: EntityIds,
        bulkActionId: string,
    ): void => {
        const entityKey = RollupCountManager.getEntityKey(type, ids)
        this.bulkActionIdsPerEntity[entityKey]?.delete(bulkActionId)

        if (this.bulkActionIdsPerEntity[entityKey]?.size === 0) {
            const stateId = RollupCountManager.getStateId(type, ids)
            this.dispatch(setBulkActionInProgress({ type, id: stateId, inProgress: false }))
            delete this.bulkActionIdsPerEntity[entityKey]
        }

        // Schedule debounced refresh after bulk action (matches individual action behavior)
        this.entitiesToRefresh[accessReviewId] = this.entitiesToRefresh[accessReviewId] || new Set()
        this.entitiesToRefresh[accessReviewId].add(entityKey)
        this.debouncedRefreshFn()
    }

    public updateCountsOptimistically = (type: EntityType, ids: EntityIds): void => {
        const stateId = RollupCountManager.getStateId(type, ids)
        this.dispatch(incrementCompletedCount({ type, id: stateId }))
    }

    public updateCountsByAmount = (type: EntityType, ids: EntityIds, amount: number): void => {
        const stateId = RollupCountManager.getStateId(type, ids)
        const entityKey = RollupCountManager.getEntityKey(type, ids)

        // Record timestamp to prevent stale responses from overwriting
        this.optimisticUpdateTimestamps[entityKey] = Date.now()

        this.dispatch(updateCountsByAmount({ type, id: stateId, amount }))
    }

    private static getStateId(type: EntityType, ids: EntityIds): string {
        switch (type) {
            case 'user':
                return ids.userId || ''
            case 'app':
                return ids.appId || ''
            case 'appUser':
                return getAppUserCountKey(ids.appId || '', ids.appUserId || '')
            case 'appEntitlement':
                return appEntKey(ids.appId || '', ids.appEntitlementId || '')
            default:
                throw new Error(`Unknown entity type: ${type}`)
        }
    }

    public immediateRefresh = async (accessReviewId: string, type: EntityType, ids: EntityIds): Promise<void> => {
        const entityKey = RollupCountManager.getEntityKey(type, ids)
        const requestVersion = ++this.reqNumber
        const k = `${accessReviewId}:${entityKey}`

        this.dirtyEntities.delete(k)
        this.clock[k] = this.clock[k] || { version: requestVersion, timestamp: Date.now() }

        await this.fetchEntityCounts(accessReviewId, type, [ids], requestVersion)
    }

    private executeRefresh = async (): Promise<void> => {
        for (const [accessReviewId, entityKeys] of Object.entries(this.entitiesToRefresh)) {
            // Clear dirty flags for all entities being refreshed
            // This must happen BEFORE fetching to allow shouldAcceptResponse to accept the responses
            for (const entityKey of Array.from(entityKeys)) {
                const k = `${accessReviewId}:${entityKey}`
                this.dirtyEntities.delete(k)
            }

            // Group entities by type
            const byType: Record<EntityType, EntityIds[]> = {
                user: [],
                app: [],
                appUser: [],
                appEntitlement: [],
            }

            for (const entityKey of Array.from(entityKeys)) {
                const [type, ...idParts] = entityKey.split(':')
                const ids = RollupCountManager.parseEntityKey(type as EntityType, idParts)
                if (ids) {
                    byType[type as EntityType].push(ids)
                }
            }

            // Fetch counts for each type
            const requestVersion = ++this.reqNumber
            await Promise.all([
                byType.user.length > 0 && this.fetchEntityCounts(accessReviewId, 'user', byType.user, requestVersion),
                byType.app.length > 0 && this.fetchEntityCounts(accessReviewId, 'app', byType.app, requestVersion),
                byType.appUser.length > 0 &&
                this.fetchEntityCounts(accessReviewId, 'appUser', byType.appUser, requestVersion),
                byType.appEntitlement.length > 0 &&
                this.fetchEntityCounts(accessReviewId, 'appEntitlement', byType.appEntitlement, requestVersion),
            ])
        }

        // Clear the refresh queue
        this.entitiesToRefresh = {}
    }

    private static parseEntityKey(type: EntityType, idParts: string[]): EntityIds | null {
        switch (type) {
            case 'user':
                return { userId: idParts[0] }
            case 'app':
                return { appId: idParts[0] }
            case 'appUser':
                return { appId: idParts[0], appUserId: idParts[1] }
            case 'appEntitlement':
                return { appId: idParts[0], appEntitlementId: idParts[1] }
            default:
                return null
        }
    }

    // Maximum number of refs per API request, enforced by max_items validation on the
    // `refs` field of AccessReviewSearchServiceEntitlementRollupRequest (and similar messages)
    // in protos/c1api/c1/api/accessreview/v1/accessreview_search.proto
    private static readonly MAX_REFS_PER_REQUEST = 50

    private async fetchEntityCounts(
        accessReviewId: string,
        type: EntityType,
        entities: EntityIds[],
        requestVersion: number,
    ): Promise<void> {
        // Batch entities into chunks to respect API validation limits
        if (entities.length > RollupCountManager.MAX_REFS_PER_REQUEST) {
            const chunks: EntityIds[][] = []
            for (let i = 0; i < entities.length; i += RollupCountManager.MAX_REFS_PER_REQUEST) {
                chunks.push(entities.slice(i, i + RollupCountManager.MAX_REFS_PER_REQUEST))
            }
            await Promise.all(
                chunks.map((chunk) => this.fetchEntityCounts(accessReviewId, type, chunk, requestVersion)),
            )
            return
        }

        // Create a key for this request to deduplicate
        const entityKeys = entities.map((e) => RollupCountManager.getEntityKey(type, e)).sort().join(',')
        const requestKey = `${accessReviewId}:${type}:${entityKeys}`

        // If this exact request is already in flight, return the existing promise
        const existingRequest = this.inFlightRequests.get(requestKey)
        if (existingRequest) {
            return existingRequest
        }

        // Create new request
        const requestPromise = (async (): Promise<void> => {
            try {
                switch (type) {
                    case 'user':
                        await this.fetchUserCounts(accessReviewId, entities, requestVersion)
                        break
                    case 'app':
                        await this.fetchAppCounts(accessReviewId, entities, requestVersion)
                        break
                    case 'appUser':
                        await this.fetchAppUserCounts(accessReviewId, entities, requestVersion)
                        break
                    case 'appEntitlement':
                        await this.fetchAppEntitlementCounts(accessReviewId, entities, requestVersion)
                        break
                    default:
                        throw new Error(`Unknown entity type: ${type}`)
                }
            } catch (e) {
                // Silently fail - counts will be stale but UI still works
                console.error(`Failed to fetch ${type} counts:`, e)
            } finally {
                // Remove from in-flight requests once complete
                this.inFlightRequests.delete(requestKey)
            }
        })()

        this.inFlightRequests.set(requestKey, requestPromise)
        return requestPromise
    }

    private async fetchUserCounts(
        accessReviewId: string,
        entities: EntityIds[],
        requestVersion: number,
    ): Promise<void> {
        const userIds = entities.map((e) => e.userId).filter((id) => id && id !== UnknownUserID) as string[]

        if (userIds.length > 0) {
            const req = AccessReviewSearchServiceUserRollupRequest.create({
                accessReviewId,
                reviewerUserId: this.reviewerUserId,
                refs: userIds.map((id) => AccessReviewSearchServiceUserRollupRef.create({ id })),
            })

            const response = await this.accessReviewSearchServiceClient.userRollup(req).then((r) => r.response)
            const rollups = response?.list || []

            for (const rollup of rollups) {
                const k = `${accessReviewId}:user:${rollup.id}`
                if (!this.shouldAcceptResponse(k, requestVersion)) continue

                this.dispatch(
                    setRollupCounts({
                        users: {
                            [rollup.id]: {
                                openCount: computeOpenCount(rollup.openTaskCount, rollup.pendingSubmissionCount),
                                completedCount: rollup.completedTasksCount,
                                pendingCount: rollup.pendingSubmissionCount ?? 0,
                            },
                        },
                    }),
                )
                this.entitiesToRefresh[accessReviewId]?.delete(`user:${rollup.id}`)
            }
        }

        // Handle UnknownUserID separately
        if (entities.some((e) => e.userId === UnknownUserID)) {
            const unknownReq = AccountsWithNoIdentityTaskStatusRequest.create({
                accessReviewId,
                reviewerUserId: this.reviewerUserId,
            })

            const unknownResponse = await this.accessReviewSearchServiceClient.accountsWithNoIdentityTaskStatus(
                unknownReq,
            ).then((r) => r.response)
            const data = unknownResponse

            const k = `${accessReviewId}:user:${UnknownUserID}`
            if (this.shouldAcceptResponse(k, requestVersion) && data) {
                this.dispatch(
                    setRollupCounts({
                        users: {
                            [UnknownUserID]: {
                                openCount: data.hasOpenTasks ? 1 : 0,
                                completedCount: data.hasCompletedTasks ? 1 : 0,
                            },
                        },
                    }),
                )
                this.entitiesToRefresh[accessReviewId]?.delete(`user:${UnknownUserID}`)
            }
        }
    }

    private async fetchAppCounts(
        accessReviewId: string,
        entities: EntityIds[],
        requestVersion: number,
    ): Promise<void> {
        const appIds = entities.map((e) => e.appId).filter((id) => id) as string[]
        const req = SearchAppRollupRequest.create({
            accessReviewId,
            reviewerUserId: this.reviewerUserId,
            refs: appIds.map((appId) => AccessReviewAppNavRollupRef.create({ appId })),
        })

        const response = await this.accessReviewSearchServiceClient.searchAppRollup(req).then((r) => r.response)
        const rollups = response?.list || []

        for (const rollup of rollups) {
            const appId = rollup.app?.app?.id
            if (!appId) continue

            const k = `${accessReviewId}:app:${appId}`
            if (!this.shouldAcceptResponse(k, requestVersion)) continue

            this.dispatch(
                setRollupCounts({
                    apps: {
                        [appId]: {
                            openCount: computeOpenCount(rollup.openTaskCount, rollup.pendingSubmissionCount),
                            completedCount: rollup.completedTasksCount,
                            pendingCount: rollup.pendingSubmissionCount ?? 0,
                        },
                    },
                }),
            )
            this.entitiesToRefresh[accessReviewId]?.delete(`app:${appId}`)
        }
    }

    private async fetchAppUserCounts(
        accessReviewId: string,
        entities: EntityIds[],
        requestVersion: number,
    ): Promise<void> {
        const req = SearchAppUserRollupRequest.create({
            accessReviewId,
            reviewerUserId: this.reviewerUserId,
            refs: entities.map((e) =>
                AccessReviewAppUserDetailRowSearchRef.create({ appId: e.appId, appUserId: e.appUserId })),
        })

        const response = await this.accessReviewSearchServiceClient.searchAppUserRollup(req).then((r) => r.response)
        const rollups = response?.list || []

        for (const rollup of rollups) {
            const appId = rollup.appUser?.appUser?.appId
            const appUserId = rollup.appUser?.appUser?.id
            if (!appId || !appUserId) continue

            const k = `${accessReviewId}:appUser:${appId}:${appUserId}`
            if (!this.shouldAcceptResponse(k, requestVersion)) continue

            this.dispatch(
                setRollupCounts({
                    appUsers: {
                        [getAppUserCountKey(appId, appUserId)]: {
                            openCount: computeOpenCount(rollup.openTaskCount, rollup.pendingSubmissionCount),
                            completedCount: rollup.completedTasksCount,
                            pendingCount: rollup.pendingSubmissionCount ?? 0,
                        },
                    },
                }),
            )
            this.entitiesToRefresh[accessReviewId]?.delete(`appUser:${appId}:${appUserId}`)
        }
    }

    private async fetchAppEntitlementCounts(
        accessReviewId: string,
        entities: EntityIds[],
        requestVersion: number,
    ): Promise<void> {
        const req = AccessReviewSearchServiceEntitlementRollupRequest.create({
            accessReviewId,
            userId: this.reviewerUserId,
            refs: entities.map((e) =>
                AccessReviewSearchServiceEntitlementRollupRef.create({
                    appId: e.appId,
                    appEntitlementId: e.appEntitlementId,
                })),
        })

        const response = await this.accessReviewSearchServiceClient.entitlementRollup(req).then((r) => r.response)
        const rollups = response?.list || []

        for (const rollup of rollups) {
            for (const ent of rollup.list) {
                const appId = rollup.app?.id
                const appEntitlementId = ent.appEntitlement?.id
                if (!appId || !appEntitlementId) continue

                const k = `${accessReviewId}:appEntitlement:${appId}:${appEntitlementId}`
                if (!this.shouldAcceptResponse(k, requestVersion)) continue

                this.dispatch(
                    setRollupCounts({
                        appEntitlements: {
                            [appEntKey(appId, appEntitlementId)]: {
                                openCount: computeOpenCount(ent.assignedTasks, ent.pendingSubmissionCount),
                                completedCount: ent.completedTasksCount,
                                pendingCount: ent.pendingSubmissionCount ?? 0,
                            },
                        },
                    }),
                )
                this.entitiesToRefresh[accessReviewId]?.delete(`appEntitlement:${appId}:${appEntitlementId}`)
            }
        }
    }

    private shouldAcceptResponse(key: string, requestVersion: number): boolean {
        // Check if entity is dirty
        if (this.dirtyEntities.has(key)) {
            return false
        }

        // Extract entity key (remove accessReviewId prefix)
        const parts = key.split(':')
        const entityKey = parts.slice(1).join(':')

        // Don't accept responses for entities with recent optimistic updates
        const optimisticTime = this.optimisticUpdateTimestamps[entityKey]
        if (optimisticTime && Date.now() - optimisticTime < 5000) {
            // Optimistic update is less than 5 seconds old, reject stale backend response
            return false
        }

        // Clean up old timestamp
        if (optimisticTime) {
            delete this.optimisticUpdateTimestamps[entityKey]
        }

        // Check version
        const stored = this.clock[key]
        if (stored && requestVersion < stored.version) {
            return false
        }

        // Update clock
        this.clock[key] = { version: requestVersion, timestamp: Date.now() }
        return true
    }

    public fetchInitialCounts = async (
        accessReviewId: string,
        fetchOptions?: Array<'user' | 'app' | 'appUser' | 'appEntitlement'>,
    ): Promise<void> => {
        const requestVersion = ++this.reqNumber

        // Default to fetching all types if not specified
        const typesToFetch = fetchOptions ?? ['user', 'app', 'appUser', 'appEntitlement']

        try {
            // Fetch only requested rollup types in parallel
            const [userRollups, appRollups, appUserRollups, entitlementRollups] = await Promise.all([
                // User rollups
                typesToFetch.includes('user')
                    ? this.accessReviewSearchServiceClient.userRollup(
                        AccessReviewSearchServiceUserRollupRequest.create({
                            accessReviewId,
                            reviewerUserId: this.reviewerUserId,
                            pageSize: 100,
                        }),
                    ).then((r) => r.response)
                    : Promise.resolve(undefined),
                // App rollups
                typesToFetch.includes('app')
                    ? this.accessReviewSearchServiceClient.searchAppRollup(
                        SearchAppRollupRequest.create({
                            accessReviewId,
                            reviewerUserId: this.reviewerUserId,
                            pageSize: 100,
                        }),
                    ).then((r) => r.response)
                    : Promise.resolve(undefined),
                // AppUser rollups
                typesToFetch.includes('appUser')
                    ? this.accessReviewSearchServiceClient.searchAppUserRollup(
                        SearchAppUserRollupRequest.create({
                            accessReviewId,
                            reviewerUserId: this.reviewerUserId,
                            pageSize: 100,
                        }),
                    ).then((r) => r.response)
                    : Promise.resolve(undefined),
                // Entitlement rollups
                typesToFetch.includes('appEntitlement')
                    ? this.accessReviewSearchServiceClient.entitlementRollup(
                        AccessReviewSearchServiceEntitlementRollupRequest.create({
                            accessReviewId,
                            userId: this.reviewerUserId,
                            pageSize: 100,
                        }),
                    ).then((r) => r.response)
                    : Promise.resolve(undefined),
            ])

            // Build counts objects from responses
            const users: { [userId: string]: RollupCount } = {}
            const apps: { [appId: string]: RollupCount } = {}
            const appUsers: { [key: string]: RollupCount } = {}
            const appEntitlements: { [key: string]: RollupCount } = {}

            // Process user rollups
            for (const rollup of userRollups?.list || []) {
                users[rollup.id] = {
                    openCount: computeOpenCount(rollup.openTaskCount, rollup.pendingSubmissionCount),
                    completedCount: rollup.completedTasksCount,
                    pendingCount: rollup.pendingSubmissionCount ?? 0,
                }
            }

            // Process app rollups
            for (const rollup of appRollups?.list || []) {
                const appId = rollup.app?.app?.id
                if (!appId) continue
                apps[appId] = {
                    openCount: computeOpenCount(rollup.openTaskCount, rollup.pendingSubmissionCount),
                    completedCount: rollup.completedTasksCount,
                    pendingCount: rollup.pendingSubmissionCount ?? 0,
                }
            }

            // Process appUser rollups
            for (const rollup of appUserRollups?.list || []) {
                const appId = rollup.appUser?.appUser?.appId
                const appUserId = rollup.appUser?.appUser?.id
                if (!appId || !appUserId) continue
                const key = getAppUserCountKey(appId, appUserId)

                appUsers[key] = {
                    openCount: computeOpenCount(rollup.openTaskCount, rollup.pendingSubmissionCount),
                    completedCount: rollup.completedTasksCount,
                    pendingCount: rollup.pendingSubmissionCount ?? 0,
                }
            }

            // Process entitlement rollups
            for (const rollup of entitlementRollups?.list || []) {
                for (const ent of rollup.list) {
                    const appId = rollup.app?.id
                    const appEntitlementId = ent.appEntitlement?.id
                    if (!appId || !appEntitlementId) continue
                    const key = appEntKey(appId, appEntitlementId)
                    appEntitlements[key] = {
                        openCount: computeOpenCount(ent.assignedTasks, ent.pendingSubmissionCount),
                        completedCount: ent.completedTasksCount,
                        pendingCount: ent.pendingSubmissionCount ?? 0,
                    }
                }
            }

            // Session check removed - was comparing this.sessionId to itself

            // Set all counts in Redux at once
            this.dispatch(
                setRollupCounts({
                    users,
                    apps,
                    appUsers,
                    appEntitlements,
                }),
            )

            // Update clock for all fetched entities
            const now = Date.now()
            Object.keys(users).forEach((userId) => {
                const k = `${accessReviewId}:user:${userId}`
                this.clock[k] = { version: requestVersion, timestamp: now }
            })
            Object.keys(apps).forEach((appId) => {
                const k = `${accessReviewId}:app:${appId}`
                this.clock[k] = { version: requestVersion, timestamp: now }
            })
            Object.keys(appUsers).forEach((key) => {
                const [appId, appUserId] = key.split(':')
                const k = `${accessReviewId}:appUser:${appId}:${appUserId}`
                this.clock[k] = { version: requestVersion, timestamp: now }
            })
            Object.keys(appEntitlements).forEach((key) => {
                const { appId, appEntitlementId } = aekDecoded(key)
                const k = `${accessReviewId}:appEntitlement:${appId}:${appEntitlementId}`
                this.clock[k] = { version: requestVersion, timestamp: now }
            })
        } catch (e) {
            // Silently fail - components will lazy-load counts as needed
            console.error('Failed to fetch initial rollup counts:', e)
        }
    }

    public fetchSpecificCounts = async (
        accessReviewId: string,
        type: EntityType,
        ids: EntityIds,
    ): Promise<void> => {
        const requestVersion = ++this.reqNumber
        await this.fetchEntityCounts(accessReviewId, type, [ids], requestVersion)
    }

    public fetchBatchCounts = async (
        accessReviewId: string,
        type: EntityType,
        entitiesList: EntityIds[],
    ): Promise<void> => {
        if (entitiesList.length === 0) return
        const requestVersion = ++this.reqNumber
        await this.fetchEntityCounts(accessReviewId, type, entitiesList, requestVersion)
    }

    public clearCounts = (): void => {
        this.dispatch({ type: 'optimisticRollupCounts/clearRollupCounts' })
        this.dirtyEntities.clear()
        this.entitiesToRefresh = {}
        this.ticketActionIdsPerEntity = {}
        this.bulkActionIdsPerEntity = {}
        this.inFlightRequests.clear()
        this.clock = {}
        this.sessionId = Date.now().toString()
    }
}
